import { Text, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { usePecField } from 'components/form'
import { useAlergiaSelectFieldQuery } from 'graphql/hooks.generated'
import { CriticidadeAlergia, PrescricaoSdcDto, ReceitaMedicamentoSdcDto } from 'graphql/types.generated'
import React, { useCallback, useMemo } from 'react'
import { parseNumber } from 'util/number'
import { meta } from 'view/atendimentos/atendimento-individual/model'

import { PrescricaoSdcCheckbox } from './PrescricaoSdcCheckbox'

interface PrescricaoGroupProps {
  prescricao: PrescricaoSdcDto
  handleTogglePrescricao: (prescricao: ReceitaMedicamentoSdcDto) => void
  idadeCidadaoEmAnos?: number
  prontuarioId: ID
}

export function SdcPrescricaoGroup(props: PrescricaoGroupProps) {
  const { prescricao, handleTogglePrescricao, idadeCidadaoEmAnos = 0, prontuarioId } = props

  const {
    input: { value: alergiasAtendimento },
  } = usePecField({ name: meta.avaliacao.alergias })

  const { data: alergiasPersistidas } = useAlergiaSelectFieldQuery({
    variables: {
      filtro: {
        prontuarioId,
      },
    },
    skip: !prontuarioId,
  })

  const alergiaPorCriticidadePersistidas = useMemo(() => {
    const map = new Map<number, CriticidadeAlergia>()
    const alergiasBanco = alergiasPersistidas?.alergias?.content ?? []

    alergiasBanco.forEach((alergia) => {
      const principioAtivoId = alergia.substanciaEspecifica?.coPrincipioAtivo
      let criticidade = alergia.criticidade

      if (!criticidade) {
        criticidade = CriticidadeAlergia.NAO_INFORMADA
      }

      if (principioAtivoId && criticidade) {
        map.set(principioAtivoId, criticidade)
      }
    })

    return map
  }, [alergiasPersistidas])

  const alergiaPorCriticidadeAtendimento = useMemo(() => {
    const map = new Map<number, CriticidadeAlergia>()

    const ArrayAlergiasAtendimento = Array.isArray(alergiasAtendimento) ? alergiasAtendimento : []

    ArrayAlergiasAtendimento.forEach((a) => {
      const principioAtivo =
        a?.substanciaMedicamento?.principioAtivo?.id && parseNumber(a?.substanciaMedicamento?.principioAtivo?.id)
      let criticidade = a?.criticidade

      if (!criticidade) {
        criticidade = CriticidadeAlergia.NAO_INFORMADA
      }
      if (principioAtivo && criticidade) {
        map.set(principioAtivo, criticidade)
      }
    })

    return map
  }, [alergiasAtendimento])

  const alergiaPorCriticidade = useMemo(() => {
    const map = new Map<number, CriticidadeAlergia>()

    alergiaPorCriticidadePersistidas.forEach((criticidade, principioAtivo) => {
      map.set(principioAtivo, criticidade)
    })
    alergiaPorCriticidadeAtendimento.forEach((criticidade, principioAtivo) => {
      map.set(principioAtivo, criticidade)
    })

    return map
  }, [alergiaPorCriticidadeAtendimento, alergiaPorCriticidadePersistidas])

  const isAlergicoAndCriticidade = useCallback(
    (medicamento: ReceitaMedicamentoSdcDto) => {
      const principioAtivoId = medicamento.medicamentoCatmat?.principioAtivo.id
        ? parseNumber(medicamento.medicamentoCatmat?.principioAtivo.id)
        : null
      const criticidade = principioAtivoId != null ? alergiaPorCriticidade.get(principioAtivoId) : undefined

      return {
        isAlergico: principioAtivoId != null && alergiaPorCriticidade.has(principioAtivoId),
        criticidade: criticidade ?? CriticidadeAlergia.NAO_INFORMADA,
      }
    },
    [alergiaPorCriticidade]
  )

  return prescricao.medicamentos.length > 0 ? (
    <Box>
      <VFlow vSpacing={0.5}>
        <Text fontWeight='bold'>{`Para ${prescricao.titulo}`}</Text>
        {prescricao.medicamentos.length > 1 && <Text>É recomendável escolher entre:</Text>}
        {prescricao.medicamentos.map((medicamento, index) => (
          <PrescricaoSdcCheckbox
            dataTestId='PrescricaoCheckbox.medicamento'
            key={index}
            prescricao={medicamento}
            handleTogglePrescricao={handleTogglePrescricao}
            idadeCidadaoEmAnos={idadeCidadaoEmAnos}
            {...isAlergicoAndCriticidade(medicamento)}
          />
        ))}
      </VFlow>
    </Box>
  ) : (
    <></>
  )
}
